import { Injectable } from '@angular/core';

export interface DbTable {
  name: string;
}

export interface DataSet {
  name: string;
  data: DataRow[];
}

export interface DataRow {
  row: any;
  key: number | string;
}

@Injectable()
/* This used to contain methods to store/retrieve data from a SQLITE web db. This has since been refactored to now store the data
   in memory instead, so we don't need to keep serializing/deserializing data - which makes the app much faster in general, especially
   daily log validation. I have kept the old method names and general structure so i don't have to refactor other app pages (which
   is why lots of methods are async and return a promise when they don't really need to be). */
export class DbContext {
  public tables: DataSet[] = [
    {name: 'Metadata', data: []},
    {name: 'Setup', data: []},
    {name: 'OperationsData', data: []},
    {name: 'StepCollectionsData', data: []},
    {name: 'DailyLogData', data: []},
    {name: 'ConsumableData', data: []},
    {name: 'WeatherSetup', data: []},
    {name: 'WeatherLogData', data: []},
    {name: 'WeatherRemarkData', data: []},
    {name: 'VesselCrewData', data: []},
    {name: 'OvertimeRequestedBysData', data: []},
    {name: 'VesselMetadata', data: []},
    {name: 'Vessel', data: []},
    {name: 'ProjectSpecificOperationInstance', data: []},
    {name: 'SetupChangeLogData', data: []},
    {name: 'KnownUsers', data: []},
    {name: 'KnownUserMappings', data: []},
    {name: 'TimeZoneData', data: []},
    {name: 'JackingActivities', data: []},
    {name: 'FieldPositionings', data: []},
    {name: 'SeaStates', data: []},
    {name: 'DeepWellTieIns', data: []},
    {name: 'SeaWaterSuction', data: []},
    {name: 'LegLoadLogData', data: []},
    {name: 'DailyObservationData', data: []},
    {name: 'OnSiteMovement', data: []},
    {name: 'OnSiteMovementTypes', data: []},
    {name: 'OnSiteMovementStatus', data: []},
    {name: 'VoyageLogData', data: []},
    {name: 'MorningMinutesData', data: []},
  ];

  constructor() {
  }

  getDataSetFromDataSetList(table: any): DataSet {
    const dataSetIndex = this.tables.findIndex(e => e.name == table.name);
    if (dataSetIndex != -1) {
      return this.tables[dataSetIndex];
    }

    return null;
  }

  async get(table: any, key: number | string): Promise<any> {
    const dataSet = this.getDataSetFromDataSetList(table) as DataSet;

    if (!dataSet || !dataSet.data) {
      return null;
    }

    const rowIndex = dataSet.data.findIndex(e => e.key == key);

    if (rowIndex != -1) {
      return Promise.resolve(dataSet.data[rowIndex].row);
    }

    return Promise.resolve(null);
  }

  async getManyIn(table: any, keys: number[] | string[]): Promise<any> {
    const dataSet = this.getDataSetFromDataSetList(table);

    if (!dataSet || !dataSet.data) {
      return Promise.resolve(null);
    }

    return Promise.resolve(dataSet.data.filter(e => keys.some(k => k == e.key)).map(d => d.row));
  }

  async getAll(table: any): Promise<any> {
    const dataSet = this.getDataSetFromDataSetList(table);

    if (!dataSet || !dataSet.data) {
      return null;
    }

    return Promise.resolve(dataSet.data.map(d => d.row));
  }

  async addOrUpdate(table: any, key: number | string, dataToAdd: any) {
    const dataSetIndex = this.tables.findIndex(e => e.name == table.name);

    if (dataSetIndex != -1) {
      if (this.tables[dataSetIndex].data == null) {
        this.tables[dataSetIndex].data = [];
      }

      const existingDataToAddIndex = this.tables[dataSetIndex].data.findIndex(e => e.key == key);

      if (existingDataToAddIndex != -1) {
        this.tables[dataSetIndex].data[existingDataToAddIndex].row = dataToAdd;
      } else {
        const dataRow: DataRow = {
          key: key,
          row: dataToAdd
        };

        this.tables[dataSetIndex].data.push(dataRow);
      }
    }
  }

  remove(table: any, key: number) {
    const dataSetIndex = this.tables.findIndex(e => e.name == table.name);

    if (dataSetIndex != -1) {
      if (this.tables[dataSetIndex].data == null) {
        this.tables[dataSetIndex].data = [];
      }

      const dataToRemoveIndex = this.tables[dataSetIndex].data.findIndex(e => e.key == key);

      if (dataToRemoveIndex != -1) {
        this.tables[dataSetIndex].data.splice(dataToRemoveIndex, 1);
      }
    }
  }

  removeAll(table: any) {
    const dataSetIndex = this.tables.findIndex(e => e.name == table.name);

    if (dataSetIndex != -1) {
      this.tables[dataSetIndex].data = [];
    }
  }

  async getTempId(table): Promise<any> {
    const dataSet = this.getDataSetFromDataSetList(table);

    if (!dataSet || !dataSet.data) {
      return null;
    }

    let minId = -1;
    for (let i = 0; i < dataSet.data.length; i++) {
      const currentId = parseInt(dataSet.data[i].key as any);
      minId = (currentId == null || isNaN(currentId) || currentId >= 0) ? -1 : (currentId - 1);
    }

    return Promise.resolve(minId);
  }

  deleteAllDataForVersionUpdate() {
    this.dropAllTables();
  }

  dropAllTables() {
    for (let i = 0; i < this.tables.length; i++) {
      this.tables[i].data = [];
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/data/dbcontext.service.ts